import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import RetirementsPanel from "../components/RetirementsPanel";
import { setRetirement } from "../services/retirements";
import {
  createContext,
  ContextDeletionRefusal,
  deleteContext,
  listContexts,
  renameContext,
  retryContextVault,
} from "../services/contexts";
import type { BrowserContextSummary } from "../types/context";
import {
  formatContextDate,
  formatContextStatus,
  formatProfileSize,
} from "../types/context";
import "./ContextsPage.css";
import "../styles/ResourceSections.css";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function contextDeletionBlocked(context: BrowserContextSummary): boolean {
  return context.session_count > 0 || context.vault_status !== undefined;
}

function explainContextDeletionRefusal(
  context: BrowserContextSummary,
  refusal?: ContextDeletionRefusal,
): string {
  const reasons: string[] = [];
  if (refusal?.code === "context_retained_vault" || context.vault_status !== undefined) {
    reasons.push("Ce contexte conserve un coffre protégé qui bloque sa suppression.");
  }
  const count = refusal?.blockingSessions.length || context.session_count;
  if (count > 0) {
    reasons.push(`${count} session${count > 1 ? "s conservées bloquent" : " conservée bloque"} la suppression du contexte.`);
  }
  if (refusal?.blockingSessions.some(session =>
    session.checkpoint === "failed" || session.checkpoint === "error" || session.checkpoint === null)) {
    reasons.push("Une sauvegarde est en échec ou sans état confirmé ; sa protection contre la destruction reste active.");
  }
  reasons.push("L’archivage récupérable masque ce contexte et conserve ses profils et son coffre. Les sessions doivent être arrêtées et leurs opérations terminées.");
  return reasons.join(" ");
}

export default function ContextsPage() {
  const [contexts, setContexts] = useState<BrowserContextSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deletionIssue, setDeletionIssue] = useState<{ id: string; uncertain: boolean } | null>(null);
  const deletionRequests = useRef(new Set<string>());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const refreshed = await listContexts();
      setContexts(refreshed);
      // A still-visible row does not prove that a previous DELETE has finished.
      // Only disappearance settles that outcome; a GET never authorizes replay.
      for (const id of deletionRequests.current) {
        if (!refreshed.some(context => context.id === id)) deletionRequests.current.delete(id);
      }
      setDeletionIssue(current => current?.uncertain && refreshed.some(context => context.id === current.id) ? current : null);
      setError(deletionRequests.current.size > 0
        ? "Suppression encore non confirmée. Le contexte reste visible ; faites vérifier l’issue de la demande avant de la répéter."
        : null);
    } catch (cause: unknown) {
      setError(errorMessage(cause, "Impossible de charger les contextes."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!contexts.some(context => context.vault_status === "pending")) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void listContexts().then(next => { if (!cancelled) setContexts(next); }).catch(() => {
        if (!cancelled) setError("État du coffre indisponible. Actualisez pour réessayer.");
      });
    }, 2000);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [contexts]);

  const createNamedContext = useCallback(async (contextName: string) => {
    const trimmed = contextName.trim();
    if (trimmed.length === 0) {
      setError("Le nom du contexte est requis.");
      return;
    }
    setBusy(true);
    try {
      const created = await createContext(trimmed);
      setContexts((current) => [created, ...current]);
      setName("");
      setError(null);
    } catch (cause: unknown) {
      setError(errorMessage(cause, "Impossible de créer le contexte."));
    } finally {
      setBusy(false);
    }
  }, []);

  const saveRename = useCallback(async (contextId: string) => {
    const trimmed = editName.trim();
    if (trimmed.length === 0) {
      setError("Le nom du contexte est requis.");
      return;
    }
    setBusy(true);
    try {
      const updated = await renameContext(contextId, trimmed);
      setContexts((current) =>
        current.map((context) => (context.id === contextId ? updated : context)),
      );
      setEditingId(null);
      setError(null);
    } catch (cause: unknown) {
      setError(errorMessage(cause, "Impossible de renommer le contexte."));
    } finally {
      setBusy(false);
    }
  }, [editName]);

  const removeContext = useCallback(async (context: BrowserContextSummary) => {
    if (deletionRequests.current.has(context.id)) return;
    // These observed blockers are already sufficient for the server to refuse.
    // Offer the recoverable action before asking for a destructive confirmation.
    if (contextDeletionBlocked(context)) {
      setDeletionIssue({ id: context.id, uncertain: false });
      setError(explainContextDeletionRefusal(context));
      return;
    }
    const containsSavedProfile =
      context.current_revision > 0 || context.profile_size_bytes > 0;
    const confirmation = containsSavedProfile
      ? `Supprimer le contexte « ${context.name} » ?\n\nCe contexte contient un profil/login sauvegardé. Le contexte et son historique seront supprimés de l’application ; le profil sera conservé dans une archive opérateur, sans restauration depuis cette page. Pour une restauration utilisateur, choisissez Archiver. Continuer ?`
      : `Supprimer le contexte « ${context.name} » ?`;
    if (!window.confirm(confirmation)) return;
    deletionRequests.current.add(context.id);
    setBusy(true);
    setDeletionIssue(null);
    let unresolved = true;
    try {
      await deleteContext(context.id);
      unresolved = false;
      setContexts((current) => current.filter((item) => item.id !== context.id));
      setError(null);
    } catch (cause: unknown) {
      if (cause instanceof ContextDeletionRefusal) {
        unresolved = false;
        setDeletionIssue({ id: context.id, uncertain: false });
        setError(explainContextDeletionRefusal(context, cause));
      } else {
        setDeletionIssue({ id: context.id, uncertain: true });
        setError("Suppression non confirmée. Actualisez pour observer l’état des contextes, sans répéter la demande ; aucun nouvel essai automatique.");
      }
    } finally {
      if (!unresolved) deletionRequests.current.delete(context.id);
      setBusy(false);
    }
  }, []);

  const refusedContext = deletionIssue && !deletionIssue.uncertain
    ? contexts.find(context => context.id === deletionIssue.id)
    : undefined;

  const archiveContext = async (context: BrowserContextSummary) => {
    if (busy || deletionRequests.current.has(context.id)) return;
    if (context.active_session_count > 0 || context.status === "syncing") {
      setError("Arrêtez les sessions et laissez leurs opérations se terminer avant d’archiver ce contexte. Les données sont conservées.");
      return;
    }
    if (!window.confirm(`Archiver « ${context.name} » et masquer ses sessions arrêtées ? Profils, coffre et clés sont conservés ; les accès de services seront révoqués. Aucune ressource physique n’est libérée. Restauration possible depuis les archives.`)) return;
    setBusy(true);
    try {
      await setRetirement("context", context.id, true);
      setContexts(current => current.filter(item => item.id !== context.id));
      setDeletionIssue(null);
      setError(null);
    } catch (cause) { setError(errorMessage(cause, "Archive non confirmée. Actualisez avant de réessayer.")); }
    finally { setBusy(false); }
  };

  return (
    <main className="contexts-shell">
      <div className="contexts-page">
        <header className="contexts-header">
          <div>
            <h1 className="contexts-title">Contextes navigateur</h1>
            <p className="contexts-counter" aria-label="Nombre de contextes">
              {contexts.length} contextes visibles
            </p>
          </div>
          <button className="contexts-button contexts-button-secondary" disabled={busy || loading} onClick={() => void load()}>
            Actualiser
          </button>
        </header>

        <RetirementsPanel onRestored={() => void load()} />

        <p className="contexts-counter" aria-label="Modèle contexte et session">
          Chaque session appartient à un seul contexte. Le nombre de contextes et de sessions n’est pas limité par un quota.
        </p>

        {error !== null && (
          <div className="contexts-banner contexts-banner-error" role="alert">
            {error}
            {refusedContext && (
              <div className="contexts-card-actions">
                <Link className="contexts-link-button" to={`/contexts/${refusedContext.id}/sessions`}>
                  Voir les sessions de {refusedContext.name}
                </Link>
                <button
                  className="contexts-button contexts-button-secondary"
                  disabled={busy || refusedContext.active_session_count > 0 || refusedContext.status === "syncing"}
                  onClick={() => void archiveContext(refusedContext)}
                >
                  Archiver « {refusedContext.name} » (récupérable)
                </button>
              </div>
            )}
          </div>
        )}

        {loading && <div className="contexts-banner" role="status">Chargement des contextes…</div>}

        {!loading && contexts.length === 0 && error === null && (
          <section className="contexts-empty" aria-labelledby="contexts-empty-title">
            <h2 id="contexts-empty-title">Aucun contexte navigateur</h2>
            <p>Créez votre premier profil persistant. Rien n’est créé automatiquement.</p>
            <button
              className="contexts-button"
              disabled={busy}
              onClick={() => void createNamedContext("Personnel")}
            >
              Créer « Personnel »
            </button>
          </section>
        )}

        {!loading && contexts.length > 0 && (
          <section
            className="resource-section"
            aria-labelledby="browser-contexts-section-title"
          >
            <header className="resource-section-header">
              <div>
                <h2
                  id="browser-contexts-section-title"
                  className="resource-section-title"
                >
                  Contextes navigateur ({contexts.length})
                </h2>
                <p className="resource-section-description">
                  Profils persistants et sessions rattachées, isolés par propriétaire.
                </p>
              </div>
              <span className="resource-section-count" aria-hidden="true">
                {contexts.length}
              </span>
            </header>
            <div className="contexts-grid" aria-label="Liste des contextes">
              {contexts.map((context) => (
                <article className="resource-card contexts-card" key={context.id}>
                  <div className="contexts-card-header">
                    <h2 className="contexts-card-title">{context.name}</h2>
                    <span className={`contexts-status contexts-status-${context.status}`}>
                      {formatContextStatus(context.status)}
                    </span>
                  </div>
                  <dl className="contexts-metrics">
                    <div className="contexts-metric">
                      <dt>Sessions</dt>
                      <dd>
                        {context.active_session_count} actives · {context.session_count} enregistrées
                      </dd>
                    </div>
                    <div className="contexts-metric"><dt>Profil</dt><dd>{formatProfileSize(context.profile_size_bytes)}</dd></div>
                    <div className="contexts-metric"><dt>Révision</dt><dd>#{context.current_revision}</dd></div>
                    <div className="contexts-metric"><dt>Dernière sauvegarde</dt><dd>{formatContextDate(context.last_saved_at)}</dd></div>
                    <div className="contexts-metric"><dt>Sessions</dt><dd>Chrome et Firefox — même coffre privé</dd></div>
                    {context.vault_status && <div className="contexts-metric"><dt>Coffre</dt><dd>{context.vault_status === "ready" ? "Coffre prêt" : context.vault_status === "pending" ? "Coffre en préparation" : "Coffre en erreur"}</dd></div>}
                    <div className="contexts-metric"><dt>Mis à jour</dt><dd>{formatContextDate(context.updated_at)}</dd></div>
                  </dl>
                  {context.vault_error && <p role="status">{context.vault_error}</p>}
                  {contextDeletionBlocked(context) && (
                    <p>Suppression bloquée par les sessions ou le coffre conservés. L’archivage récupérable garde les données.</p>
                  )}
                  {context.vault_retryable && context.vault_status === "error" && <button className="contexts-button" disabled={busy} onClick={() => {
                    setBusy(true);
                    void retryContextVault(context.id).then(updated => setContexts(current => current.map(item => item.id === updated.id ? {...updated, vault_status: "pending"} : item)))
                      .catch(() => setError("Impossible de relancer le coffre."))
                      .finally(() => setBusy(false));
                  }}>Réessayer le coffre</button>}

                  {editingId === context.id ? (
                    <form
                      className="contexts-rename"
                      aria-label={`Renommer ${context.name}`}
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveRename(context.id);
                      }}
                    >
                      <input
                        className="contexts-input"
                        aria-label="Nouveau nom"
                        value={editName}
                        maxLength={100}
                        onChange={(event) => setEditName(event.target.value)}
                      />
                      <button className="contexts-button" type="submit" disabled={busy}>Enregistrer</button>
                      <button className="contexts-button contexts-button-secondary" type="button" onClick={() => setEditingId(null)}>Annuler</button>
                    </form>
                  ) : (
                    <div className="contexts-card-actions">
                      <Link className="contexts-link-button" to={`/contexts/${context.id}/sessions`}>
                        Voir les sessions de {context.name}
                      </Link>
                      <button
                        className="contexts-button contexts-button-secondary"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(context.id);
                          setEditName(context.name);
                        }}
                      >
                        Renommer
                      </button>
                      <button
                        className="contexts-button contexts-button-danger"
                        disabled={busy || (deletionIssue?.id === context.id && deletionIssue.uncertain)}
                        title={contextDeletionBlocked(context) ? "Voir les raisons du refus et les actions possibles" : undefined}
                        onClick={() => void removeContext(context)}
                      >
                        Supprimer
                      </button>
                      {refusedContext?.id !== context.id && (
                        <button className="contexts-button contexts-button-secondary" disabled={busy || context.active_session_count > 0 || context.status === "syncing" || (deletionIssue?.id === context.id && deletionIssue.uncertain)} onClick={() => void archiveContext(context)}>
                          Archiver (données conservées)
                        </button>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        <form
          className="resource-section contexts-form"
          aria-label="Créer un contexte"
          onSubmit={(event) => {
            event.preventDefault();
            void createNamedContext(name);
          }}
        >
          <header className="resource-section-header">
            <div>
              <h2 className="resource-section-title">Créer un contexte</h2>
              <p className="resource-section-description">
                Ajoutez un profil persistant avant de créer ses sessions.
              </p>
            </div>
          </header>
          <div className="contexts-form-row">
            <label className="contexts-field">
              Nom
              <input
                className="contexts-input"
                value={name}
                maxLength={100}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
                placeholder="Ex. Travail"
              />
            </label>
            <button className="contexts-button" type="submit" disabled={busy || name.trim().length === 0}>
              Créer
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
